import { getUserInfo } from './auth';
import { getApiV1Url } from '../config/api';

export const NOTIFICATION_CHANNEL_OPTIONS = [
  { value: 'EMAIL', label: '이메일' },
  { value: 'SMS', label: 'SMS' },
  { value: 'PUSH', label: '푸시' },
  { value: 'ALIMTALK', label: '알림톡' },
] as const;

export type NotificationChannel = (typeof NOTIFICATION_CHANNEL_OPTIONS)[number]['value'];

export interface NotificationChannelSetting {
  projectCode: string;
  channel: string;
  senderKey: string;
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface ApiErrorBody {
  timestamp?: number;
  message?: string;
  errors?: {
    error?: Array<{
      code: number;
      target: string;
      message: string;
    }>;
  };
  data?: unknown;
}

interface NotificationChannelSettingListResponse extends ApiErrorBody {
  data: {
    totalCount: number;
    notificationChannelSettings: NotificationChannelSetting[];
  };
}

interface NotificationChannelSettingDetailResponse extends ApiErrorBody {
  data: NotificationChannelSetting;
}

export interface NotificationChannelSettingCreateRequest {
  channel: string;
  senderKey: string;
}

export interface NotificationChannelSettingUpdateRequest {
  senderKey: string;
}

export function getNotificationChannelLabel(channel: string): string {
  return NOTIFICATION_CHANNEL_OPTIONS.find((option) => option.value === channel)?.label ?? channel;
}

function getAuthHeaders(): Record<string, string> {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }
  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
}

function throwIfApiError(result: ApiErrorBody, response: Response): void {
  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((error) => error.message).join(', ');
    throw new Error(errorMessage);
  }
  if (!response.ok) {
    throw new Error(result.message || `HTTP error! status: ${response.status}`);
  }
}

/**
 * 알림 채널 설정 목록 조회
 * GET /api/v1/projects/{projectCode}/notification-channel-settings?channel=
 */
export const getNotificationChannelSettingList = async (
  projectCode: string,
  channel?: string
): Promise<{
  notificationChannelSettings: NotificationChannelSetting[];
  totalCount: number;
}> => {
  const queryParams = new URLSearchParams();
  if (channel) queryParams.append('channel', channel);

  const query = queryParams.toString();
  const response = await fetch(
    getApiV1Url(
      `/projects/${projectCode}/notification-channel-settings${query ? `?${query}` : ''}`
    ),
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  const result: NotificationChannelSettingListResponse = await response.json().catch(() => ({}));
  throwIfApiError(result, response);

  return {
    notificationChannelSettings: result.data?.notificationChannelSettings ?? [],
    totalCount: result.data?.totalCount ?? 0,
  };
};

/**
 * 알림 채널 설정 상세 조회
 * GET /api/v1/projects/{projectCode}/notification-channel-settings/{channel}
 */
export const getNotificationChannelSetting = async (
  projectCode: string,
  channel: string
): Promise<NotificationChannelSetting | null> => {
  const response = await fetch(
    getApiV1Url(
      `/projects/${projectCode}/notification-channel-settings/${encodeURIComponent(channel)}`
    ),
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  const result: NotificationChannelSettingDetailResponse = await response.json().catch(() => ({}));
  throwIfApiError(result, response);

  return result.data ?? null;
};

/**
 * 알림 채널 설정 생성
 * POST /api/v1/projects/{projectCode}/notification-channel-settings
 */
export const createNotificationChannelSetting = async (
  projectCode: string,
  body: NotificationChannelSettingCreateRequest
): Promise<{ success: boolean; data?: NotificationChannelSetting }> => {
  const response = await fetch(
    getApiV1Url(`/projects/${projectCode}/notification-channel-settings`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        channel: body.channel.trim(),
        senderKey: body.senderKey.trim(),
      }),
    }
  );

  const result: NotificationChannelSettingDetailResponse = await response.json().catch(() => ({}));
  throwIfApiError(result, response);

  return { success: true, data: result.data };
};

/**
 * 알림 채널 설정 수정
 * POST /api/v1/projects/{projectCode}/notification-channel-settings/{channel}
 */
export const updateNotificationChannelSetting = async (
  projectCode: string,
  channel: string,
  body: NotificationChannelSettingUpdateRequest
): Promise<{ success: boolean; data?: NotificationChannelSetting }> => {
  const response = await fetch(
    getApiV1Url(
      `/projects/${projectCode}/notification-channel-settings/${encodeURIComponent(channel)}`
    ),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        senderKey: body.senderKey.trim(),
      }),
    }
  );

  const result: NotificationChannelSettingDetailResponse = await response.json().catch(() => ({}));
  throwIfApiError(result, response);

  return { success: true, data: result.data };
};
